import type { CaseStudy } from "@/types/content";

/**
 * Concept case studies — realistic, production-shaped placeholders.
 * Every entry is a CONCEPT PROJECT (no real client data). The schema matches
 * what a real engagement would need, so swapping in live work later requires
 * zero template changes.
 */
export const caseStudies: CaseStudy[] = [
  {
    dashboard: "erp",
    slug: "typical-erp",
    title: "AI Powered Enterprise Resource Management",
    category: "Enterprise Platform",
    industry: "Manufacturing",
    accent: "#0F766E",
    cardSummary:
      "Modernise enterprise operations through one intelligent ERP platform.",
    outcomeHighlight:
      "Unified finance, procurement, inventory and manufacturing into one AI-powered operating platform.",
    transformation:
      "Enterprise Transformation",
    
    summary:
      "An AI-powered enterprise platform that unifies finance, procurement, inventory, manufacturing and logistics into a single intelligent operating system for modern businesses.",
    tech: ["React", "TypeScript", "FastAPI", "PostgreSQL", "Redis"],
    overview:
      "This concept project demonstrates how VibeWell approaches enterprise digital transformation for growing manufacturing organisations. Rather than replacing one legacy application at a time, we reimagined the entire operational ecosystem as a unified, cloud-native platform. By combining workflow automation, AI-assisted decision support and real-time operational intelligence, every department works from the same trusted data while leadership gains complete visibility into business performance.",
    challenge: [
      "Disconnected ERP, inventory and finance applications creating duplicate data",
      "Manual procurement, approvals and reconciliation delaying operations",
      "Limited visibility into inventory, production and financial performance",
      "Reactive decision-making due to outdated reports and spreadsheet-driven processes",
      ],
    approach: [
      {
        title:"Business Process Discovery",
        description:"Mapped operational workflows across procurement, inventory, manufacturing, finance and logistics to identify automation opportunities."
      },

      {
        title:"Unified Enterprise Platform",
        description:"Designed a modular platform where every department shares one real-time operational data model."
      },

      {
        title:"AI & Workflow Automation",
        description:"Automated approvals, procurement recommendations, exception alerts and repetitive operational workflows."
      },

      {
        title:"Executive Intelligence",
        description:"Built live operational dashboards with predictive analytics, KPI monitoring and AI-assisted business insights."
      },

    ],
    solutionHighlights:[
      "Unified finance, procurement and inventory platform",

      "AI-assisted demand forecasting and procurement recommendations",

      "Workflow automation across purchasing, approvals and fulfilment",

      "Real-time operational dashboards for executives",

      "Role-based access with secure audit trails",

      "API-first architecture for third-party integrations",

    ],
    results:[
      {
      label:"Operational efficiency",
      value:"+42%"
      },

      {
      label:"Manual processing",
      value:"-80%"
      },

      {
      label:"Decision speed",
      value:"3×"
      },

      {
      label:"Systems consolidated",
      value:"7 → 1"
      },
    ],
    duration: "16-week concept build",
    services: ["erp-development", "web-applications", "workflow-automation"],
    businessOutcome:
      "Created a single intelligent enterprise platform that reduced operational complexity, automated critical business workflows and enabled faster executive decision-making through real-time operational intelligence.",
  },
  {
    dashboard: "healthcare",
    slug: "typical-hms",
    title: "Hospital Management System",
    category: "Healthcare Platform",
    industry: "Healthcare",
    accent: "#06B6D4",
    cardSummary:
      "Transform patient care through connected digital healthcare operations.",

    outcomeHighlight:
      "Connected appointments, diagnostics and patient engagement within one secure healthcare ecosystem.",

    transformation:
      "Digital Healthcare",
    summary:
      "An AI-enabled healthcare operations platform that unifies appointments, electronic medical records, diagnostics, pharmacy, billing and patient engagement into one secure digital ecosystem.",
    tech: ["React Native", "Node.js", "GraphQL", "AWS"],
    overview:
      "This concept project demonstrates how VibeWell modernises healthcare delivery through a secure, cloud-native digital platform. Instead of treating appointments, clinical records, laboratories, billing and patient communication as separate systems, we designed a connected healthcare ecosystem where information flows securely between departments. AI-assisted scheduling, operational dashboards and workflow automation help clinicians spend less time on administration and more time delivering quality patient care.",
    challenge: [
      "Fragmented clinical and administrative systems creating duplicate patient information",

      "Manual appointment scheduling leading to long waiting times and missed consultations",

      "Disconnected laboratory, pharmacy and billing workflows",

      "Limited operational visibility across departments for hospital management",
    ],
    approach: [
      {
        title:"Healthcare Process Discovery",
        description:"Mapped patient journeys across registration, consultation, diagnostics, pharmacy and billing to identify operational bottlenecks."
      },

      {
        title:"Unified Clinical Platform",
        description:"Designed a secure digital platform connecting every operational department through a shared patient record."
      },

      {
        title:"AI & Intelligent Automation",
        description:"Introduced AI-assisted appointment scheduling, automated reminders and workflow automation across routine administrative processes."
      },

      {
        title:"Operational Intelligence",
        description:"Built executive dashboards providing live visibility into patient flow, departmental workload and service performance."
      },
    ],
    solutionHighlights:[

      "Unified electronic medical record platform",

      "AI-assisted appointment scheduling",

      "Integrated laboratory, pharmacy and billing workflows",

      "Real-time hospital operations dashboard",

      "Secure role-based access with complete audit history",

      "Mobile-first patient engagement experience",

    ],
    results:[
      {label:"Administrative effort", value:"-55%"},

      {label:"Patient wait time", value:"-35%"},

      {label:"Appointment efficiency", value:"+48%"},

      {label:"Department visibility", value:"100%"},
    ],
    duration: "14-week concept build",
    services: ["mobile-app-development", "ui-ux-design", "api-development"],
    businessOutcome:
      "Unified clinical and administrative operations into a secure digital platform that improved patient experience, streamlined hospital workflows and provided real-time operational visibility for healthcare leadership.",
  },
  {
    dashboard: "crm",
    slug: "typical-crm",
    title: "Customer Relationship Management Platform",
    category: "Business Platform",
    industry: "Professional Services",
    accent: "#6366F1",

    cardSummary:
      "Increase sales productivity with AI-assisted customer intelligence.",
    outcomeHighlight:
      "Enabled revenue teams to close opportunities faster through AI-powered sales workflows.",
    transformation:
      "Revenue Intelligence",
    summary:
      "An AI-powered customer relationship platform that combines sales automation, revenue intelligence, pipeline forecasting and customer engagement into one intelligent workspace.",
    tech: ["React", "Python", "OpenAI", "Redis", "PostgreSQL"],
    overview:
      "This concept project demonstrates how VibeWell transforms traditional CRM systems into intelligent revenue platforms. Instead of simply recording customer interactions, the platform continuously analyses pipeline health, automates routine activities and assists sales teams with AI-generated insights, allowing organisations to focus on building stronger customer relationships while improving forecast accuracy and operational efficiency.",
    challenge:[
      "Sales teams spending excessive time on manual CRM administration",

      "Inconsistent customer information across multiple communication channels",

      "Unreliable pipeline forecasting limiting executive planning",

      "Missed follow-ups reducing conversion opportunities",
    ],
    approach:[
      {
        title:"Sales Process Analysis",
        description:"Mapped customer acquisition workflows to identify repetitive manual activities suitable for automation."
      },

      {
        title:"Unified Customer Intelligence",
        description:"Created a complete customer timeline combining communication history, opportunities and sales activities."
      },

      {
        title:"AI Sales Assistance",
        description:"Integrated AI-generated meeting summaries, lead prioritisation, follow-up drafting and pipeline insights."
      },

      {
        title:"Revenue Intelligence",
        description:"Designed executive dashboards with live forecasting, pipeline analytics and performance monitoring."
      },
    ],
    solutionHighlights:[
      "AI-generated meeting summaries",

      "Intelligent lead scoring and prioritisation",

      "Automated follow-up recommendations",

      "Real-time revenue forecasting",

      "Unified customer interaction timeline",

      "Workflow automation for repetitive sales tasks",
    ],
    results:[
      {label:"Sales productivity", value:"+38%"},

      {label:"Manual CRM work", value:"-60%"},

      {label:"Forecast accuracy", value:"+35%"},

      {label:"Lead response time", value:"-45%"},
    ],
    duration: "12-week concept build",
    services: ["crm-development", "ai-integration", "web-applications"],
    businessOutcome:
      "Created an intelligent revenue platform that improved sales productivity, automated customer engagement workflows and enabled leadership to make faster, data-driven revenue decisions.",
  },
  {
    dashboard: "retail",
    slug: "typical-ecommerce",
    title: "AI-Powered Omnichannel Commerce Platform",
    category: "E-commerce",
    industry: "Retail",
    accent: "#F59E0B",
    cardSummary:
      "Deliver seamless omnichannel commerce powered by AI.",

    outcomeHighlight:
      "Unified digital and physical retail operations with intelligent customer engagement.",

    transformation:
      "Omnichannel Commerce",
    summary:
      "An AI-powered omnichannel commerce platform that unifies online sales, physical stores, inventory, customer engagement and analytics into one intelligent retail ecosystem.",
    tech: ["React", "Tailwind", "Stripe", "Vercel"],
    overview:
      "This concept project demonstrates how VibeWell helps retailers modernise customer experience across digital and physical channels. Rather than treating e-commerce, inventory, marketing and customer engagement as separate systems, we designed a unified commerce platform that provides real-time inventory visibility, AI-assisted merchandising and intelligent customer engagement while enabling leadership to make faster, data-driven business decisions.",
    challenge: [
      "Disconnected online, in-store and warehouse inventory systems",

      "Inconsistent customer experience across sales channels",

      "Limited visibility into purchasing behaviour and product demand",

      "Manual merchandising and promotional workflows reducing business agility",
    ],
    approach: [
      {
        title:"Commerce Strategy",
        description:"Analysed customer journeys across online and offline channels to identify opportunities for operational improvement."
      },

      {
        title:"Unified Commerce Platform",
        description:"Designed a cloud-native platform connecting e-commerce, inventory, customer engagement and order fulfilment."
      },

      {
        title:"AI & Intelligent Automation",
        description:"Introduced AI-assisted product recommendations, demand forecasting and automated promotional workflows."
      },

      {
        title:"Retail Intelligence",
        description:"Developed executive dashboards providing real-time visibility into sales performance, inventory health and customer behaviour."
      },
    ],
    solutionHighlights:[
      "Unified omnichannel inventory management",

      "AI-powered product recommendations",

      "Demand forecasting and inventory optimisation",

      "Automated promotional campaign management",

      "Real-time sales and customer analytics",

      "API-first architecture for retail integrations",
    ],
    results:[
      {label:"Sales growth", value:"+34%"},

      {label:"Inventory accuracy", value:"+98%"},

      {label:"Order fulfilment", value:"+41%"},

      {label:"Customer satisfaction", value:"+27%"},
    ],
    duration: "10-week concept build",
    services: ["website-development", "web-applications", "cloud-deployment"],
    businessOutcome:
      "One intelligent commerce platform that improved customer experience, optimised inventory management and enabled data-driven retail decision-making.",
  },
  {
    dashboard: "education",
    slug: "typical-school-erp",
    title: "Smart Education Management Platform",
    category: "Education Platform",
    industry: "Education",
    accent: "#8B5CF6",
    cardSummary:
      "Build an AI-enabled digital campus connecting students, teachers and parents.",

    outcomeHighlight:
      "Modernised academic and administrative operations through one connected education platform.",

    transformation:
      "Smart Education",
    summary:
      "An AI-enabled education platform that connects admissions, academics, finance, communication and student engagement into one intelligent digital campus.",
    tech: ["React", "FastAPI", "PostgreSQL", "WebSockets"],
    overview:
      "This concept project demonstrates how VibeWell helps educational institutions modernise academic and administrative operations through a secure digital campus platform. Instead of relying on disconnected admissions systems, spreadsheets and manual communication, we designed a unified education ecosystem where students, teachers, parents and administrators collaborate through a single intelligent platform enhanced with workflow automation and AI-assisted academic insights.",
    challenge:[

      "Admissions, academics and finance managed across disconnected systems",

      "Manual administrative processes reducing staff productivity",

      "Inconsistent communication between teachers, students and parents",

      "Limited visibility into academic performance and institutional operations",

    ],
    approach:[
      {
        title:"Education Process Discovery",
        description:"Analysed academic and administrative workflows to identify opportunities for digital transformation."
      },

      {
        title:"Unified Digital Campus",
        description:"Designed an integrated platform connecting admissions, academics, attendance, finance and communication."
      },

      {
        title:"AI & Workflow Automation",
        description:"Automated admissions, attendance tracking, fee notifications and academic reporting while introducing AI-assisted student performance insights."
      },

      {
        title:"Institutional Intelligence",
        description:"Delivered executive dashboards enabling school leadership to monitor academic, financial and operational performance in real time."
      },
    ],
    solutionHighlights:[
      "Unified admissions and student lifecycle management",

      "AI-assisted academic performance insights",

      "Automated attendance and communication workflows",

      "Integrated fee and financial management",

      "Real-time institutional performance dashboards",

      "Mobile-first parent, student and teacher portal",
    ],
    results:[
      {label:"Administrative efficiency", value:"+48%"},

      {label:"Admission processing", value:"-65%"},

      {label:"Parent engagement", value:"+52%"},

      {label:"Attendance accuracy", value:"+99%"},
    ],
    duration: "15-week concept build",
    services: ["web-applications", "ui-ux-design", "api-development"],
    businessOutcome:
      "Created a connected digital campus that streamlined educational administration, strengthened communication among students, parents and educators, and enabled institutional leaders to make faster, data-driven academic and operational decisions.",
  },
  {
    dashboard: "logistics",
    slug: "typical-logistics",
    title: "Intelligent Logistics Operations Platform",
    category: "Operations Platform",
    industry: "Logistics",
    accent: "#0EA5E9",
    cardSummary:
      "Optimise logistics through intelligent fleet and warehouse management.",

    outcomeHighlight:
      "Delivered real-time logistics visibility through AI-assisted operational intelligence.",

    transformation:
      "Supply Chain Intelligence",
    summary:
      "An AI-powered logistics operations platform that unifies fleet management, warehouse operations, shipment tracking and delivery intelligence into one real-time operational control centre.",
    tech: ["React", "Python", "Kafka", "MongoDB", "Mapbox"],
    overview:
      "This concept project demonstrates how VibeWell modernises logistics operations through a cloud-native command centre that connects warehouse operations, transportation, fleet management and customer deliveries. Instead of relying on disconnected dispatch systems and reactive decision-making, the platform provides real-time operational visibility, workflow automation and AI-assisted route optimisation, enabling logistics teams to deliver faster, operate more efficiently and respond proactively to operational exceptions.",
    challenge: [

      "Limited real-time visibility across fleet, warehouse and delivery operations",

      "Manual dispatching and route planning reducing operational efficiency",

      "Disconnected transportation and warehouse systems creating inconsistent shipment information",

      "Reactive exception handling increasing delivery delays and customer dissatisfaction",

    ],
    approach: [

      {
        title:"Operational Assessment",
        description:"Analysed transportation, warehouse and dispatch workflows to identify opportunities for automation and operational optimisation."
      },

      {
        title:"Unified Logistics Platform",
        description:"Designed a single operational platform connecting fleet, warehouse, inventory and shipment management."
      },

      {
        title:"AI & Workflow Automation",
        description:"Implemented AI-assisted route optimisation, automated dispatch workflows, predictive ETA calculations and proactive operational alerts."
      },

      {
        title:"Operational Intelligence",
        description:"Delivered executive dashboards providing real-time visibility into fleet utilisation, warehouse performance and delivery KPIs."
      },

    ],
    solutionHighlights:[

      "Real-time fleet and shipment visibility",

      "AI-assisted route optimisation",

      "Automated dispatch and delivery workflows",

      "Predictive delivery and ETA monitoring",

      "Warehouse and transport integration",

      "Executive logistics performance dashboards",

    ],
    results:[
      {label:"Delivery efficiency", value:"+36%"},

      {label:"Fleet utilisation", value:"+24%"},

      {label:"Dispatch effort", value:"-58%"},

      {label:"On-time delivery", value:"+31%"},
    ],
    duration: "18-week concept build",
    services: ["web-applications", "workflow-automation", "cloud-deployment"],
    businessOutcome:
      "Created a unified logistics operations platform that improved operational visibility, optimised fleet utilisation, automated dispatch workflows and enabled proactive, data-driven logistics management across the entire supply chain.",
  },
];

const bySlug = new Map(caseStudies.map((c) => [c.slug, c]));

export function getCaseStudy(slug?: string): CaseStudy | undefined {
  return slug ? bySlug.get(slug) : undefined;
}
